import { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector } from '@legendapp/state/react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Folders, Home, Inbox, Layers3, Library, PanelLeftOpen, Pin, PinOff, Plus } from 'lucide-react';
import {
  getListsArray, createList, renameList, deleteList, deleteListWithArticles, auth$, getListMembers, removeMember, logout,
  articles$, articleLists$, addArticleToList, removeArticleFromList, getArticleListIds, getArticleCountForList, getListArticleCounts,
  // UI State
  ui$, setSidebarOpen, toggleSidebarResponsive, openModal, openUploadModal,
  // Decks
  getDecksArray, getDeckById, getDueCountForDeck, moveCardToDeck, renameDeck, deleteDeck,
} from '../store';
import { closeUsageModal } from '../store/ui$';
import { showErrorAlert, ErrorCategory } from '../lib/errorHandler';
import { promptForNewDeck } from '../lib/decks';
import { useScrollLock } from '../hooks/useScrollLock';
import { useSubscription } from '../hooks/useSubscription';
import { useTrialStatus } from '../hooks/useFeatureAccess';
import DeckConfigModal from './DeckConfigModal';
import ReadingHeatmap from './ReadingHeatmap';
import UsageModal from './UsageModal';
import { MembersPanel, InviteModal } from './ListMembers';
import SidebarListItem from './SidebarListItem';
import SidebarDeckItem from './SidebarDeckItem';
import SidebarFooterMenu from './SidebarFooterMenu';
import UserAvatar from './UserAvatar';
import SyncIndicator from './SyncIndicator';
import './Sidebar.css';
import { ICON_SIZE } from '../design-system';
import { isSmallMobile } from '../constants/breakpoints';
import reedleFullLogoDark from '../../../../icons/reedle/full-logo-dark.png';
import reedleFullLogoLight from '../../../../icons/reedle/full-logo-light.png';
import reedleLogoMark from '../assets/logo.svg';

const COLLAPSED_LIST_LIMIT = 5;

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['sidebar', 'common']);

  // UI state from global observable
  const isOpen = useSelector(() => ui$.sidebarOpen.get());
  const isCollapsed = useSelector(() => ui$.sidebarCollapsed?.get() ?? false);
  const colorTheme = useSelector(() => ui$.colorTheme.get());
  const sidebarActionLabel = isSmallMobile()
    ? t('common:layout.toggleMenu')
    : t(isCollapsed ? 'common:layout.pinSidebar' : 'common:layout.unpinSidebar');
  const viewingUserId = useSelector(() => ui$.viewingUserId.get());
  const currentUserId = useSelector(() => auth$.user.get()?.id);
  const currentUsername = useSelector(() => {
    const user = auth$.user.get();
    return user?.username || user?.email?.split('@')[0] || '';
  });
  const heatmapScope = useSelector(() => ui$.heatmapScope.get());

  // Lock scroll when sidebar is open (mobile with overlay)
  useScrollLock(isOpen);

  // Subscription for plan display
  const { subscription } = useSubscription();
  const { isInTrial } = useTrialStatus();

  // Determine active item from current path
  const isDashboardActive = location.pathname === '/dashboard';
  const isAllActive = location.pathname === '/all';
  const isInboxActive = location.pathname === '/inbox';
  const activeListId = location.pathname.startsWith('/list/') ? location.pathname.replace('/list/', '') : null;
  const activeDeckId = location.pathname.startsWith('/deck/') ? location.pathname.replace('/deck/', '') : null;

  // Local component state (transient UI)
  const [newListName, setNewListName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [threadsExpanded, setThreadsExpanded] = useState(() => Boolean(activeListId));
  const [reviewExpanded, setReviewExpanded] = useState(() => Boolean(activeDeckId));
  const [listsExpanded, setListsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingListId, setEditingListId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [usageModalOpen, setUsageModalOpen] = useState(false);
  const [usageModalTab, setUsageModalTab] = useState('balance');

  // Sync with global ui$ usage modal state (allows UpgradePrompt to open from anywhere)
  const globalUsageModalOpen = useSelector(() => ui$.usageModalOpen.get());
  const globalUsageModalTab = useSelector(() => ui$.usageModalTab.get());
  useEffect(() => {
    if (globalUsageModalOpen) {
      setUsageModalTab(globalUsageModalTab || 'balance');
      setUsageModalOpen(true);
      closeUsageModal(); // Reset global trigger after consuming
    }
  }, [globalUsageModalOpen, globalUsageModalTab]);
  const [membersPanelListId, setMembersPanelListId] = useState(null);
  const [inviteModalListId, setInviteModalListId] = useState(null);
  const [membersRefreshKey, setMembersRefreshKey] = useState(0);
  const [listRoles, setListRoles] = useState({});
  const [dragOverTarget, setDragOverTarget] = useState(null); // 'inbox' or list id
  const asideRef = useRef(null);
  const flyoutRef = useRef(null);
  const railTriggerRef = useRef(null);
  const flyoutTriggerRef = useRef(null);
  const restoreFocusRef = useRef(false);

  // The rail opens navigation as a flyout instead of widening itself: 'nav' is
  // the whole panel floating over the canvas, 'lists' and 'decks' are the two
  // sections that have a list behind their icon.
  const [flyout, setFlyout] = useState(null);
  const [flyoutTop, setFlyoutTop] = useState(0);
  // Only the desktop shell renders the sidebar, so "collapsed" is the rail —
  // except while the nav flyout stands in for the panel.
  const railMode = isCollapsed && flyout !== 'nav';

  const isRailFlyout = flyout === 'lists' || flyout === 'decks';
  const flyoutTitle = flyout === 'decks' ? t('sidebar:decks') : t('sidebar:lists');

  const openFlyout = (kind, trigger) => {
    flyoutTriggerRef.current = trigger;
    setFlyoutTop(trigger.getBoundingClientRect().top);
    setFlyout(current => (current === kind ? null : kind));
  };

  // Docking or undocking answers the question the flyout was asking.
  useEffect(() => {
    setFlyout(null);
  }, [isCollapsed]);

  // Opening the panel unmounts the button that opened it, so hand focus to the
  // navigation it revealed; Escape hands it back to whichever row it came from.
  useEffect(() => {
    if (flyout === 'nav') {
      asideRef.current
        ?.querySelector('.sidebar-nav a, .sidebar-nav button')
        ?.focus({ preventScroll: true });
      return;
    }
    if (flyout || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    const trigger = flyoutTriggerRef.current;
    (trigger?.isConnected ? trigger : railTriggerRef.current)?.focus({ preventScroll: true });
  }, [flyout]);

  useEffect(() => {
    if (!flyout) return undefined;
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      restoreFocusRef.current = true;
      setFlyout(null);
    };
    const handlePointerDown = (e) => {
      if (asideRef.current?.contains(e.target) || flyoutRef.current?.contains(e.target)) return;
      // Row menus are portalled to the body, so they are outside both surfaces
      // while still belonging to the flyout that opened them.
      if (e.target.closest?.('.sidebar-row-menu')) return;
      setFlyout(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [flyout]);


  // Deck menu state
  const [editingDeckId, setEditingDeckId] = useState(null);
  const [editingDeckName, setEditingDeckName] = useState('');
  const [deckConfigModalDeckId, setDeckConfigModalDeckId] = useState(null);

  // Drag and drop handlers for moving articles to lists
  const handleDragOver = (e, targetId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget(targetId);
  };

  const handleDragLeave = () => {
    setDragOverTarget(null);
  };

  const handleDrop = (e, targetListId) => {
    e.preventDefault();
    setDragOverTarget(null);
    const articleId = e.dataTransfer.getData('application/x-article-id');
    if (articleId) {
      if (targetListId === null) {
        // Drop to Inbox: remove from all lists
        const currentListIds = getArticleListIds(articleId);
        currentListIds.forEach(lid => removeArticleFromList(articleId, lid));
      } else {
        // Drop to list: add article to the list (multi-list)
        addArticleToList(articleId, targetListId);
      }
    }
  };

  // Clean up orphan junction entries for a list (articles that were deleted)
  // Called on list click to lazily fix stale data from before the fix
  const cleanupOrphanEntriesForList = (listId) => {
    const obj = articleLists$.get() || {};
    const articlesObj = articles$.get() || {};

    // GUARD: Don't cleanup if articles haven't synced yet
    // This prevents deleting junction entries during initial load race condition
    // (clicking list before articles$ loads would delete ALL entries)
    if (Object.keys(articlesObj).length === 0) {
      return;
    }

    for (const [entryId, entry] of Object.entries(obj)) {
      if (entry?.list_id === listId) {
        const article = articlesObj[entry.article_id];
        if (!article || article.deleted) {
          articleLists$[entryId].delete();
        }
      }
    }
  };

  // Drag and drop handlers for moving cards to decks
  const handleDeckDragOver = (e, deckId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget(`deck-${deckId}`);
  };

  const handleDeckDrop = (e, targetDeckId) => {
    e.preventDefault();
    setDragOverTarget(null);
    const cardId = e.dataTransfer.getData('application/x-card-id');
    if (cardId) {
      moveCardToDeck(cardId, targetDeckId);
    }
  };

  // Get lists from store
  const lists = useSelector(() => getListsArray());

  // Get decks from store
  const decks = useSelector(() => getDecksArray());

  // Create stable list IDs string to avoid triggering useEffect on every input
  const listIdsKey = useMemo(() => lists.map(l => l.id).join(','), [lists]);
  const visibleLists = listsExpanded ? lists : lists.slice(0, COLLAPSED_LIST_LIMIT);
  const hasHiddenLists = lists.length > COLLAPSED_LIST_LIMIT;
  const activeListIndex = useMemo(
    () => lists.findIndex(list => list.id === activeListId),
    [activeListId, lists]
  );

  // Keep a directly-opened list visible after a reload, even if it falls below the fold.
  useEffect(() => {
    if (activeListIndex >= COLLAPSED_LIST_LIMIT) {
      setListsExpanded(true);
    }
    if (activeListIndex >= 0) {
      setThreadsExpanded(true);
    }
  }, [activeListIndex]);

  useEffect(() => {
    if (activeDeckId) {
      setReviewExpanded(true);
    }
  }, [activeDeckId]);

  const articleCounts = useSelector(() => getListArticleCounts());

  // Preserve legacy settings hashes by forwarding them to the route-level page.
  useEffect(() => {
    const hash = location.hash;
    if (hash === '#settings' || hash.startsWith('#settings/')) {
      const tab = hash === '#settings' ? null : hash.replace('#settings/', '');
      navigate(tab === 'account' ? '/settings/account' : '/settings', { replace: true });
    }
  }, [location.hash, navigate]);

  // Load roles for all lists - only when list IDs actually change (not on every re-render)
  useEffect(() => {
    if (!currentUserId || lists.length === 0) return;
    const loadRoles = async () => {
      const roles = {};
      for (const list of lists) {
        try {
          const members = await getListMembers(list.id);
          const me = members.find(m => m.user_id === currentUserId);
          roles[list.id] = me?.role || 'member';
        } catch {
          roles[list.id] = 'member';
        }
      }
      setListRoles(roles);
    };
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listIdsKey, currentUserId, membersRefreshKey]); // Use listIdsKey instead of lists to prevent re-fetching on every state change

  const handleCreateList = async () => {
    if (!newListName.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const newList = await createList(newListName.trim());
      // Immediately set role as owner (don't wait for loadRoles)
      setListRoles(prev => ({ ...prev, [newList.id]: 'owner' }));
      setNewListName('');
      setIsCreating(false);
      setThreadsExpanded(true);
      setListsExpanded(true);
    } catch (err) {
      console.error('Failed to create list:', err);
      showErrorAlert(`Failed to create list: ${err.message}`, err, { component: 'Sidebar', action: 'create_list', category: ErrorCategory.GENERAL });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeaveList = async (listId) => {
    if (!confirm(t('sidebar:confirm.leaveList'))) return;
    await removeMember(listId, currentUserId);
    if (activeListId === listId) {
      navigate('/inbox');
    }
  };

  const handleRename = (listId) => {
    if (!editingName.trim()) return;
    renameList(listId, editingName.trim());
    setEditingListId(null);
    setEditingName('');
  };

  const handleDelete = (listId) => {
    const articleCount = getArticleCountForList(listId);
    openModal('confirmDelete', {
      header: t('sidebar:deleteListModal.header'),
      message: t('sidebar:deleteListModal.message'),
      confirmText: t('sidebar:deleteListModal.confirmText'),
      variant: 'primary',
      showDontAskAgain: false,
      extraCheckbox: articleCount > 0 ? {
        label: t('sidebar:deleteListModal.alsoDeleteArticles', { count: articleCount }),
        checkedConfirmText: t('sidebar:deleteListModal.confirmTextWithArticles'),
        checkedVariant: 'danger',
      } : null,
      onConfirm: (deleteArticlesToo) => {
        if (deleteArticlesToo) {
          deleteListWithArticles(listId);
        } else {
          deleteList(listId);
        }
        if (activeListId === listId) {
          navigate('/inbox');
        }
      },
    });
  };

  const startEditing = (list) => {
    setEditingListId(list.id);
    setEditingName(list.name);
  };

  // Deck handlers (reuse pattern from lists)
  const handleRenameDeck = (deckId) => {
    if (!editingDeckName.trim()) return;
    renameDeck(deckId, editingDeckName.trim());
    setEditingDeckId(null);
    setEditingDeckName('');
  };

  const handleDeleteDeck = (deckId) => {
    const deck = getDeckById(deckId);
    if (!confirm(t('sidebar:confirm.deleteDeck', { name: deck?.name }))) return;
    deleteDeck(deckId);
    if (activeDeckId === deckId) {
      navigate('/inbox');
    }
  };

  const startEditingDeck = (deck) => {
    setEditingDeckId(deck.id);
    setEditingDeckName(deck.name);
  };

  // Version modal replaces old handleForceUpdate

  const handleLogout = () => {
    if (confirm(t('sidebar:confirm.logout'))) {
      logout();
    }
  };

  const renderNewListInput = () => isCreating && (
    <div className="new-list-input">
      <input
        type="text"
        placeholder={t('sidebar:listNamePlaceholder')}
        value={newListName}
        onChange={(e) => setNewListName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !isSubmitting) handleCreateList();
          if (e.key === 'Escape') { setIsCreating(false); setNewListName(''); }
        }}
        onBlur={() => { if (!newListName.trim() && !isSubmitting) setIsCreating(false); }}
        disabled={isSubmitting}
        autoFocus
      />
    </div>
  );

  const renderListItem = (list) => (
    <SidebarListItem
      key={list.id}
      list={list}
      isActive={activeListId === list.id}
      isDragOver={dragOverTarget === list.id}
      isEditing={editingListId === list.id}
      editingName={editingName}
      onEditingNameChange={setEditingName}
      onRename={() => handleRename(list.id)}
      onCancelEdit={() => setEditingListId(null)}
      onClick={() => { cleanupOrphanEntriesForList(list.id); navigate(`/list/${list.id}`); setSidebarOpen(false); setFlyout(null); }}
      onDragOver={(e) => handleDragOver(e, list.id)}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDrop(e, list.id)}
      membersRefreshKey={membersRefreshKey}
      articleCount={articleCounts[list.id] || 0}
      listRole={listRoles[list.id]}
      onViewMembers={() => setMembersPanelListId(list.id)}
      onInviteMember={() => setInviteModalListId(list.id)}
      onStartEditing={() => startEditing(list)}
      onDelete={() => handleDelete(list.id)}
      onLeaveList={() => handleLeaveList(list.id)}
    />
  );

  const renderDeckItem = (deck) => (
    <SidebarDeckItem
      key={deck.id}
      deck={deck}
      dueCount={getDueCountForDeck(deck.id)}
      isSystem={deck.is_system}
      isActive={activeDeckId === deck.id}
      isDragOver={dragOverTarget === `deck-${deck.id}`}
      isEditing={editingDeckId === deck.id}
      editingDeckName={editingDeckName}
      onEditingNameChange={setEditingDeckName}
      onRename={() => handleRenameDeck(deck.id)}
      onCancelEdit={() => setEditingDeckId(null)}
      onClick={() => { navigate(`/deck/${deck.id}`); setSidebarOpen(false); setFlyout(null); }}
      onDeckDragOver={(e) => handleDeckDragOver(e, deck.id)}
      onDragLeave={handleDragLeave}
      onDeckDrop={(e) => handleDeckDrop(e, deck.id)}
      onOpenConfig={() => setDeckConfigModalDeckId(deck.id)}
      onStartEditing={() => startEditingDeck(deck)}
      onDelete={() => handleDeleteDeck(deck.id)}
    />
  );

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={() => { setSidebarOpen(false); setFlyout(null); }} />}

      <aside
        ref={asideRef}
        className={`sidebar ${isOpen ? 'open' : ''} ${railMode ? 'sidebar--rail' : ''} ${flyout === 'nav' ? 'sidebar--nav-flyout' : ''}`}
        data-testid="sidebar"
      >
        {/* The brand row is the sidebar's own top row, not a separate header
            box. On the rail the mark is also the control: pointing at the rail
            turns it into the panel toggle, so the one place that says "Reedle"
            is the one place that opens navigation. */}
        <div className="sidebar-brand">
          {railMode ? (
            <button
              ref={railTriggerRef}
              type="button"
              className="sidebar-rail-trigger"
              onClick={(e) => openFlyout('nav', e.currentTarget)}
              aria-label={t('common:layout.openNavigation')}
              aria-expanded={false}
              aria-controls="sidebar-nav"
              title={t('common:layout.openNavigation')}
              data-testid="sidebar-rail-trigger"
            >
              <img className="logo-compact" src={reedleLogoMark} alt="" />
              <PanelLeftOpen className="sidebar-rail-trigger-icon" size={ICON_SIZE.md} />
            </button>
          ) : (
            <>
              <Link to="/" className="logo" aria-label={t('common:login.pageTitle')}>
                <img
                  className="logo-full"
                  src={colorTheme === 'dark' ? reedleFullLogoDark : reedleFullLogoLight}
                  alt=""
                />
              </Link>
              <button
                className="sidebar-pin-control"
                onClick={toggleSidebarResponsive}
                aria-label={sidebarActionLabel}
                aria-pressed={isSmallMobile() ? undefined : !isCollapsed}
                data-testid="sidebar-toggle"
                title={sidebarActionLabel}
              >
                <Pin className="sidebar-pin-icon sidebar-pin-icon--pin" size={ICON_SIZE.sm} />
                <PinOff className="sidebar-pin-icon sidebar-pin-icon--unpin" size={ICON_SIZE.sm} />
              </button>
            </>
          )}
        </div>

        <nav id="sidebar-nav" className="sidebar-nav">
          {/* Dashboard */}
          <Link
            to="/dashboard"
            className={`menu-item ${isDashboardActive ? 'active' : ''}`}
            onClick={() => { setSidebarOpen(false); setFlyout(null); }}
            aria-current={isDashboardActive ? 'page' : undefined}
            data-testid="nav-dashboard"
          >
            <span className="menu-item-label">
              <Home size={ICON_SIZE.sm} />
              <span>{t('sidebar:dashboard')}</span>
            </span>
          </Link>

          {/* All - shows all articles */}
          <Link
            to="/all"
            className={`menu-item ${isAllActive ? 'active' : ''}`}
            onClick={() => { setSidebarOpen(false); setFlyout(null); }}
            aria-current={isAllActive ? 'page' : undefined}
            data-testid="nav-all"
          >
            <span className="menu-item-label">
              <Library size={ICON_SIZE.sm} />
              <span>{t('sidebar:all')}</span>
            </span>
            {articleCounts.all > 0 && (
              <span className="menu-count">{articleCounts.all}</span>
            )}
          </Link>

          {/* Primary capture entry — opens the global Add to Reedle modal */}
          <button
            type="button"
            className="menu-item menu-item-add"
            data-onboarding="import"
            onClick={() => { openUploadModal(); setSidebarOpen(false); setFlyout(null); }}
            data-testid="nav-add"
          >
            <span className="menu-item-label">
              <Plus size={ICON_SIZE.sm} />
              <span>{t('sidebar:add')}</span>
            </span>
          </button>

          {/* Inbox remains a first-level destination; custom lists live inside Lists. */}
          <Link
            to="/inbox"
            className={`menu-item ${isInboxActive ? 'active' : ''} ${dragOverTarget === 'inbox' ? 'drag-over' : ''}`}
            onClick={() => { setSidebarOpen(false); setFlyout(null); }}
            onDragOver={(e) => handleDragOver(e, 'inbox')}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, null)}
            aria-current={isInboxActive ? 'page' : undefined}
            data-testid="nav-inbox"
          >
            <span className="menu-item-label">
              <Inbox size={ICON_SIZE.sm} />
              <span>{t('sidebar:inbox')}</span>
            </span>
            {articleCounts.inbox > 0 && (
              <span className="menu-count">{articleCounts.inbox}</span>
            )}
          </Link>

          <section className={`sidebar-thread-group ${threadsExpanded ? 'is-expanded' : ''}`}>
            <div className="sidebar-thread-row">
              <button
                type="button"
                className={`menu-item sidebar-thread-trigger ${activeListId ? 'active' : ''}`}
                onClick={(e) => {
                  if (railMode) { openFlyout('lists', e.currentTarget); return; }
                  setThreadsExpanded(expanded => !expanded);
                }}
                aria-expanded={railMode ? flyout === 'lists' : threadsExpanded}
                aria-controls={railMode ? 'sidebar-flyout' : 'sidebar-thread-lists'}
                data-testid="threads-toggle"
              >
                <span className="menu-item-label">
                  <Folders size={ICON_SIZE.sm} />
                  <span>{t('sidebar:lists')}</span>
                </span>
                <ChevronDown className="sidebar-thread-chevron" size={ICON_SIZE.xs} />
              </button>
              <button
                type="button"
                className="menu-section-action sidebar-thread-add"
                onClick={() => { setThreadsExpanded(true); setIsCreating(true); }}
                aria-label={t('sidebar:newList')}
                title={t('sidebar:newList')}
                data-testid="new-list-button"
              >
                <Plus size={ICON_SIZE.sm} />
              </button>
            </div>

            {threadsExpanded && (
              <div id="sidebar-thread-lists" className="sidebar-thread-children">
                {!railMode && renderNewListInput()}

                {visibleLists.map(renderListItem)}

                {hasHiddenLists && (
                  <button
                    type="button"
                    className="sidebar-lists-more"
                    onClick={() => setListsExpanded(expanded => !expanded)}
                    aria-expanded={listsExpanded}
                    data-testid="lists-more-button"
                  >
                    {t(listsExpanded ? 'sidebar:showLess' : 'sidebar:showMore')}
                  </button>
                )}
              </div>
            )}
          </section>

          <section className={`sidebar-thread-group sidebar-review-group ${reviewExpanded ? 'is-expanded' : ''}`}>
            <div className="sidebar-thread-row">
              <button
                type="button"
                className={`menu-item sidebar-thread-trigger ${activeDeckId ? 'active' : ''}`}
                onClick={(e) => {
                  if (railMode) { openFlyout('decks', e.currentTarget); return; }
                  setReviewExpanded(expanded => !expanded);
                }}
                aria-expanded={railMode ? flyout === 'decks' : reviewExpanded}
                aria-controls={railMode ? 'sidebar-flyout' : 'sidebar-review-decks'}
                data-testid="review-toggle"
              >
                <span className="menu-item-label">
                  <Layers3 size={ICON_SIZE.sm} />
                  <span>{t('sidebar:decks')}</span>
                </span>
                <ChevronDown className="sidebar-thread-chevron" size={ICON_SIZE.xs} />
              </button>
              <button
                type="button"
                className="menu-section-action sidebar-thread-add"
                onClick={() => { setReviewExpanded(true); promptForNewDeck(t); }}
                aria-label={t('sidebar:newDeck')}
                title={t('sidebar:newDeck')}
                data-testid="new-deck-button"
              >
                <Plus size={ICON_SIZE.sm} />
              </button>
            </div>

            {reviewExpanded && (
              <div id="sidebar-review-decks" className="sidebar-thread-children">
                {decks.map(renderDeckItem)}
              </div>
            )}
          </section>
        </nav>

        {/* Reading Activity Heatmap */}
        <ReadingHeatmap listId={heatmapScope} userId={viewingUserId || currentUserId} />

        {/* Footer with user info and menu */}
        <div className="sidebar-footer">
          <div className="sidebar-footer-row">
            <button
              type="button"
              className="sidebar-footer-identity"
              onClick={() => { navigate('/me'); setSidebarOpen(false); setFlyout(null); }}
              aria-label={t('sidebar:me')}
            >
              <UserAvatar
                userId={currentUserId}
                username={currentUsername}
                size={24}
                className="sidebar-footer-avatar"
              />
              <span className="sidebar-footer-user">
                {currentUsername}
                {subscription?.tier && subscription.tier !== 'free' && (
                  <span
                    className={`sidebar-plan-name ${isInTrial ? 'trial' : subscription.tier}`}
                    onClick={(e) => { e.stopPropagation(); setUsageModalTab('plan'); setUsageModalOpen(true); }}
                  >
                    {isInTrial
                      ? t('common:trial.label')
                      : subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)
                    }
                  </span>
                )}
              </span>
              <SyncIndicator />
            </button>
            <div className="sidebar-footer-menu-wrapper">
              <SidebarFooterMenu
                onOpenUsage={() => setUsageModalOpen(true)}
                onLogout={() => handleLogout()}
              />
            </div>
          </div>
        </div>
      </aside>

      {isRailFlyout && (
        <div
          ref={flyoutRef}
          id="sidebar-flyout"
          className="sidebar-flyout"
          style={{ '--sidebar-flyout-top': `${flyoutTop}px` }}
          role="group"
          aria-label={flyoutTitle}
          data-testid={`sidebar-flyout-${flyout}`}
        >
          <div className="sidebar-flyout-header">
            <span className="sidebar-flyout-title">{flyoutTitle}</span>
            <button
              type="button"
              className="menu-section-action"
              onClick={() => {
                if (flyout === 'lists') { setIsCreating(true); return; }
                promptForNewDeck(t);
              }}
              aria-label={flyout === 'lists' ? t('sidebar:newList') : t('sidebar:newDeck')}
              title={flyout === 'lists' ? t('sidebar:newList') : t('sidebar:newDeck')}
              data-testid="sidebar-flyout-add"
            >
              <Plus size={ICON_SIZE.sm} />
            </button>
          </div>
          <div className="sidebar-flyout-body">
            {flyout === 'lists' ? (
              <>
                {renderNewListInput()}
                {lists.map(renderListItem)}
              </>
            ) : decks.map(renderDeckItem)}
          </div>
        </div>
      )}

      {/* Members Panel Modal */}
      <MembersPanel
        listId={membersPanelListId}
        isOpen={!!membersPanelListId}
        onClose={() => setMembersPanelListId(null)}
        onLeave={() => { navigate('/inbox'); setMembersPanelListId(null); }}
        onMembersChange={() => setMembersRefreshKey(k => k + 1)}
      />

      {/* Invite Member Modal */}
      <InviteModal
        listId={inviteModalListId}
        isOpen={!!inviteModalListId}
        onClose={() => setInviteModalListId(null)}
        onMembersChange={() => setMembersRefreshKey(k => k + 1)}
      />

      {/* Deck Config Modal */}
      <DeckConfigModal
        isOpen={!!deckConfigModalDeckId}
        onClose={() => setDeckConfigModalDeckId(null)}
        deck={deckConfigModalDeckId ? getDeckById(deckConfigModalDeckId) : null}
      />

      {/* Usage Modal */}
      <UsageModal
        isOpen={usageModalOpen}
        onClose={() => { setUsageModalOpen(false); setUsageModalTab('balance'); }}
        activeTab={usageModalTab}
        onTabChange={setUsageModalTab}
      />
    </>
  );
};

export default Sidebar;
